"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface SearchInputV2Props {
  value: string;
  onChange: (_value: string) => void;
  onSearchChange?: (_value: string) => void;
  debounceMs?: number;
  placeholder?: string;
  clearLabel?: string;
  label?: string;
  helperText?: string;
  errorText?: string;
  disabled?: boolean;
  className?: string;
}

export function SearchInputV2({
  value,
  onChange,
  onSearchChange,
  debounceMs = 300,
  placeholder = "Ara...",
  clearLabel = "Temizle",
  label,
  helperText,
  errorText,
  disabled = false,
  className,
}: SearchInputV2Props) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();

  const uid = React.useId();
  const inputId = `${uid}-search`;
  const errorId = errorText ? `${inputId}-error` : undefined;
  const helperId = !errorText && helperText ? `${inputId}-helper` : undefined;
  const describedBy =
    [errorId, helperId].filter(Boolean).join(" ") || undefined;

  // Debounced search callback
  React.useEffect(() => {
    if (!onSearchChange) return;
    timerRef.current = setTimeout(() => {
      onSearchChange(value);
    }, debounceMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, debounceMs, onSearchChange]);

  const handleClear = () => {
    onChange("");
    inputRef.current?.focus();
  };

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-xs font-semibold text-[var(--v2-text-strong)]"
        >
          {label}
        </label>
      )}

      <div className="relative">
        {/* Search icon */}
        <svg
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--v2-text-muted)]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>

        <input
          ref={inputRef}
          id={inputId}
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          aria-describedby={describedBy}
          aria-invalid={errorText ? true : undefined}
          className={cn(
            "v2-focus-ring min-h-10 w-full rounded-[var(--v2-radius-md)] border bg-white pl-9 pr-9 text-sm text-[var(--v2-text-strong)] placeholder:text-slate-400",
            errorText ? "border-red-300" : "border-[var(--v2-border)]",
            disabled && "cursor-not-allowed opacity-50"
          )}
        />

        {/* Clear button */}
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={clearLabel}
            className="v2-focus-ring absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-[var(--v2-text-muted)] transition hover:text-[var(--v2-text-strong)]"
          >
            ✕
          </button>
        )}
      </div>

      {errorText && (
        <span id={errorId} className="text-xs text-red-600" role="alert">
          {errorText}
        </span>
      )}
      {!errorText && helperText && (
        <span id={helperId} className="text-xs text-[var(--v2-text-muted)]">
          {helperText}
        </span>
      )}
    </div>
  );
}
